import type { ComponentProps } from 'react';

import Link from '@vs-shop/image-link/Link';

import { Skeleton } from '@vs-shop/ui/skeleton';
import { clsxm } from '@vs-shop/utils';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

import { FramedMedia, type FramedMediaProps } from '../../internal/framedMedia.tsx';
import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The grid a brand index is: fixed-size cards, one logo and one name each, the
 * whole card a link. Flat named parts a tenant arranges in its own JSX, each
 * painting itself.
 *
 *   <BrandIndexGridCount>{countLine}</BrandIndexGridCount>
 *
 *   <BrandIndexGridList>
 *     {brands.map((brand) => (
 *       <BrandIndexGridItem key={brand.id}>
 *         <BrandIndexCard href={brand.href} aria-label={brand.name}>
 *           {brand.image === null ? (
 *             <BrandIndexCardMediaPlaceholder><ImageMissingPlaceholder /></BrandIndexCardMediaPlaceholder>
 *           ) : (
 *             <BrandIndexCardMedia src={brand.image} alt={brand.name} width={100} height={100} />
 *           )}
 *           <BrandIndexCardName>{brand.name}</BrandIndexCardName>
 *         </BrandIndexCard>
 *       </BrandIndexGridItem>
 *     ))}
 *   </BrandIndexGridList>
 *
 * **The seam between two cards is the grid's gap, and the rounding is the
 * grid's.** The list clips at `rounded-xl` and holds its cards half a pixel
 * apart on an opaque page, so what a shopper reads as a hairline between two
 * logos is the page showing through — which is why the card paints its own
 * background and the list its own corners, and why neither of those belongs at
 * a call site.
 *
 * No `variant`: a brand index paints the same wherever it is placed. What
 * differs between tenants is how many cards there are and what they say, which
 * is data. Whether the grid renders at all, and what stands in its place when a
 * filter matches nothing, is the composition's: an empty index composes
 * `../emptyState` instead of this grid, rather than this grid branching.
 *
 * Server-first: every part is a plain server component. Nothing here holds
 * state, so a brand grid costs no client JavaScript of its own.
 */

/**
 * The count line above the grid.
 *
 * Not a heading: a brand index is furniture a page places, and the page's own
 * `<h1>` already names it. What the line SAYS — a plural count, or the name of
 * a default selection — is the tenant's words and its pluralization.
 */
const brandIndexGridCountClasses = 'mb-12 text-[28px] text-foreground';

export type BrandIndexGridCountProps = ComponentProps<'p'>;

/** The line naming what the grid is showing. */
export const BrandIndexGridCount = ({ className, ...props }: BrandIndexGridCountProps) => (
  <p
    data-slot="brand-index-grid-count"
    className={clsxm(brandIndexGridCountClasses, className)}
    {...props}
  />
);

/**
 * The cards, in as many columns as the step has room for.
 *
 * `overflow-hidden rounded-xl` with `gap-0.5` is the whole seam mechanism: the
 * cards are opaque and the gap is not, so the grid draws its own hairlines and
 * rounds the block as one. A tenant that changes the gap changes the seam.
 */
const brandIndexGridListClasses =
  'grid max-w-[1008px] grid-cols-2 gap-0.5 overflow-hidden rounded-xl tab:grid-cols-3 lap:grid-cols-4 lap-lg:grid-cols-5';

export type BrandIndexGridListProps = ComponentProps<'ul'>;

/** The card grid. */
export const BrandIndexGridList = ({ className, ...props }: BrandIndexGridListProps) => (
  <ul
    data-slot="brand-index-grid-list"
    className={clsxm(brandIndexGridListClasses, className)}
    {...props}
  />
);

export type BrandIndexGridItemProps = ComponentProps<'li'>;

/**
 * One card's cell.
 *
 * It paints nothing of its own — the card fills it and the list places it — so
 * an unstyled cell emits no `class` attribute at all rather than stamping
 * `class=""` on every cell of the grid.
 */
export const BrandIndexGridItem = ({ className, ...props }: BrandIndexGridItemProps) => (
  <li
    data-slot="brand-index-grid-item"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/**
 * The card's box.
 *
 * Fixed-size rather than fluid, at both steps: a logo grid reads as a grid only
 * while every mark sits in the same square, and a card sized by its contents
 * would give the widest wordmark a cell of its own.
 *
 * Shared with {@link BrandIndexCardSkeleton} so a card that has not arrived
 * occupies exactly the box the arrived one will — the grid does not move when
 * the answer lands.
 */
const brandIndexCardBoxClasses =
  'flex size-[160px] flex-col items-center justify-between gap-y-1 bg-background p-6 pho-lg:size-[200px]';

/** The card, and the whole of it is the click target. */
const brandIndexCardClasses = `${brandIndexCardBoxClasses} hover:shadow-xl`;

export type BrandIndexCardProps = VsLinkProps;

/**
 * One brand, as the link into it.
 *
 * The dedicated link part rather than an anchor hand-wrapped around the card:
 * whole-surface click is the same decision in every tenant, and a part is where
 * its ARIA contract is stated once. It is named by what it CONTAINS — the
 * brand's name is inside the anchor — so it needs no `aria-label`; a tenant
 * whose card carries a wordmark instead of type passes one, and it rides the
 * native prop spread.
 *
 * Renders through the `Link` seam, so a host that aliases the specifier keeps
 * client-side navigation and one that does not resolves a plain `<a>`.
 */
export const BrandIndexCard = ({ className, ...props }: BrandIndexCardProps) => (
  <Link
    data-slot="brand-index-card"
    className={clsxm(brandIndexCardClasses, className)}
    {...props}
  />
);

/**
 * The frame the logo sits in — a fixed height at each step, so a row of cards
 * holds one baseline whatever shape the marks are. Shared with
 * {@link BrandIndexCardMediaPlaceholder} so the two occupy the same box.
 */
const brandIndexCardMediaFrameClasses =
  'flex h-[80px] w-full items-center justify-center pho-lg:h-[100px]';

/** The mark itself: contained, never cropped — a brand's logo is a subject, not a texture. */
const brandIndexCardMediaImageClasses = 'h-full w-full object-contain';

export type BrandIndexCardMediaProps = FramedMediaProps;

/**
 * The brand's logo, contained in its frame.
 *
 * `src` is final and renderable: CDN, Liip and `{filter}` resolution stay
 * app-side (spec § "Media parts"), so a fixture hands a literal URL and a
 * tenant hands whatever its pipeline resolved.
 */
export const BrandIndexCardMedia = ({ className, ...props }: BrandIndexCardMediaProps) => (
  <FramedMedia
    dataSlot="brand-index-card-media"
    frameClassName={clsxm(brandIndexCardMediaFrameClasses, className)}
    imageClassName={brandIndexCardMediaImageClasses}
    {...props}
  />
);

export type BrandIndexCardMediaPlaceholderProps = ComponentProps<'div'>;

/**
 * The same frame with no logo in it — for a brand whose imagery is missing
 * before anything is requested. What goes inside is the tenant's, so whatever
 * stands in centres where the mark would have.
 */
export const BrandIndexCardMediaPlaceholder = ({
  className,
  ...props
}: BrandIndexCardMediaPlaceholderProps) => (
  <div
    data-slot="brand-index-card-media-placeholder"
    className={clsxm(brandIndexCardMediaFrameClasses, className)}
    {...props}
  />
);

/**
 * The brand's name. Two lines, then it clamps — the card is a fixed height, and
 * a third line would push the mark above it out of the box.
 */
const brandIndexCardNameClasses = 'line-clamp-2 text-center text-lg';

export type BrandIndexCardNameProps = ComponentProps<'div'>;

/** The brand's name. */
export const BrandIndexCardName = ({ className, ...props }: BrandIndexCardNameProps) => (
  <div
    data-slot="brand-index-card-name"
    className={clsxm(brandIndexCardNameClasses, className)}
    {...props}
  />
);

export type BrandIndexCardSkeletonProps = ComponentProps<'div'>;

/**
 * A card that has not arrived yet, in the card's own box so the grid's geometry
 * is the same before and after the answer.
 *
 * One part rather than three: what a loading card is made of is anatomy below
 * the line a tenant writes — a composition has nothing to put inside it and no
 * reason to reach into it.
 */
export const BrandIndexCardSkeleton = ({ className, ...props }: BrandIndexCardSkeletonProps) => (
  <div
    data-slot="brand-index-card-skeleton"
    className={clsxm(brandIndexCardBoxClasses, className)}
    {...props}
  >
    <div data-slot="brand-index-card-skeleton-media" className={brandIndexCardMediaFrameClasses}>
      <Skeleton
        data-slot="brand-index-card-skeleton-mark"
        className="bg-foreground/10 h-full w-full"
      />
    </div>
    <Skeleton
      data-slot="brand-index-card-skeleton-name"
      className="bg-foreground/10 h-5 w-3/4 rounded"
    />
  </div>
);
